import { CaretRight } from "@phosphor-icons/react";
import { useMemo, useState } from "react";

import { Button } from "common/components/ui/Button";
import { Drawer, DrawerContent } from "common/components/ui/Drawer";

import MobileListTable from "../../MobileListTable";
import TokenDetails from "./TokenDetails";

import type { ColumnDef } from "@tanstack/react-table";
import type { ApiToken } from "../../../types";

interface MobileListProps {
  tokens: ApiToken[];
  onDelete: (apiToken: ApiToken) => void;
}

export default function MobileList({ tokens, onDelete }: MobileListProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<ApiToken>(null);

  const handleDrawerState = (isOpen: boolean) => {
    setIsDrawerOpen(isOpen);
  };

  const handleRowClick = (apiToken: ApiToken) => {
    setSelectedToken(apiToken);
    setIsDrawerOpen(true);
  };

  const handleDelete = () => {
    setIsDrawerOpen(false);
    onDelete(selectedToken);
  };

  const columns: ColumnDef<ApiToken>[] = useMemo(
    () => [
      {
        id: "keyName",
        header: "Key name",
        cell: ({
          row: {
            original: { name },
          },
        }) => (
          <span className="line-clamp-1 break-words text-start text-sm-bold">
            {name}
          </span>
        ),
      },
      {
        id: "permissions",
        header: "Permission",
        cell: ({
          row: {
            original: { read: canRead, write: canWrite },
          },
        }) => {
          const permissions = canRead && canWrite ? "All" : "Read only";

          return (
            <span className="capitalize text-black-700">{permissions}</span>
          );
        },
      },
      {
        id: "actions",
        cell: () => (
          <Button intent="labelIcon" size="sm" variant="ghost-black">
            <CaretRight />
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <>
      <MobileListTable
        data={tokens}
        isLoading={false}
        columns={columns}
        onRowClick={handleRowClick}
      />
      <Drawer open={isDrawerOpen} onOpenChange={handleDrawerState}>
        <DrawerContent>
          <TokenDetails
            selectedToken={selectedToken}
            onDelete={handleDelete}
            onClose={() => setIsDrawerOpen(false)}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
}
